/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import * as React from 'react';
import React__default, { forwardRef, Children, cloneElement } from 'react';
import { useBreadcrumb } from '@zendeskgarden/container-breadcrumb';
import { retrieveComponentStyles, DEFAULT_THEME, getColor, getLineHeight, useText } from '@zendeskgarden/react-theming';
import styled, { css } from 'styled-components';
import { em } from 'polished';

const COMPONENT_ID$1 = 'breadcrumbs.list';
const StyledBreadcrumb = styled.ol.attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledBreadcrumb",
  componentId: "sc-11jrinn-0"
})(["display:flex;margin:0;padding:0;list-style:none;font-size:", ";direction:", ";", ";"], props => props.theme.fontSizes.md, props => props.theme.rtl && 'rtl', props => retrieveComponentStyles(COMPONENT_ID$1, props));
StyledBreadcrumb.defaultProps = {
  theme: DEFAULT_THEME
};

var _path;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var SvgChevronRightStroke = function SvgChevronRightStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    focusable: "false",
    viewBox: "0 0 12 12",
    "aria-hidden": "true"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M3.646 10.354a.5.5 0 01-.057-.638l.057-.07L7.293 6 3.646 2.354a.5.5 0 01-.057-.638l.057-.07a.5.5 0 01.638-.057l.07.057 4 4a.5.5 0 01.057.638l-.057.07-4 4a.5.5 0 01-.708 0z"
  })));
};

const ValidChevronIcon = props => {
  const {
    theme,
    ...validProps
  } = props;
  return React__default.createElement(SvgChevronRightStroke, validProps);
};
const StyledChevronIcon = styled(ValidChevronIcon).attrs({
  role: 'presentation',
  'aria-hidden': 'true'
}).withConfig({
  displayName: "StyledChevronIcon",
  componentId: "sc-9r9qrm-0"
})(["transform:", ";margin:0 ", ";color:", ";"], props => props.theme.rtl && `rotate(180deg);`, props => em(props.theme.space.base, props.theme.fontSizes.md), props => getColor('neutralHue', 600, props.theme));
StyledChevronIcon.defaultProps = {
  theme: DEFAULT_THEME
};

const COMPONENT_ID = 'breadcrumbs.item';
const linkStyles = _ref => {
  let {
    isCurrent
  } = _ref;
  return css(["& > :link,& > :visited{white-space:inherit;}", ""], isCurrent && `
      & > :link,
      & > :visited,
      & > :link:hover,
      & > :visited:hover,
      & > :link:focus,
      & > :visited:focus {
        color: inherit;
      }
    `);
};
const StyledBreadcrumbItem = styled.li.attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.74.0'
}).withConfig({
  displayName: "StyledBreadcrumbItem",
  componentId: "sc-r0suq7-0"
})(["line-height:", ";white-space:nowrap;color:", ";font-size:inherit;", ";", ";"], props => getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md), props => props.isCurrent ? getColor(props.theme.colors.neutralHue, 600) : 'inherit', linkStyles, props => retrieveComponentStyles(COMPONENT_ID, props));
StyledBreadcrumbItem.defaultProps = {
  theme: DEFAULT_THEME
};

const StyledCenteredBreadcrumbItem = styled(StyledBreadcrumbItem).attrs({
  'aria-hidden': true
}).withConfig({
  displayName: "StyledCenteredBreadcrumbItem",
  componentId: "sc-1ces9y2-0"
})(["display:flex;align-items:center;"]);
StyledCenteredBreadcrumbItem.defaultProps = {
  theme: DEFAULT_THEME
};

const Breadcrumb = forwardRef((props, ref) => {
  const {
    getContainerProps,
    getCurrentPageProps
  } = useBreadcrumb();
  const totalChildren = Children.count(props.children);
  const mappedChildren = Children.map(props.children, (child, index) => {
    const isLastItem = index === totalChildren - 1;
    if (isLastItem) {
      return React__default.createElement(StyledBreadcrumbItem, {
        isCurrent: true
      }, cloneElement(child, getCurrentPageProps()));
    }
    return React__default.createElement(React__default.Fragment, null, React__default.createElement(StyledBreadcrumbItem, null, child), React__default.createElement(StyledCenteredBreadcrumbItem, null, React__default.createElement(StyledChevronIcon, null)));
  });
  const ariaLabel = useText(Breadcrumb, props, 'aria-label', 'Breadcrumbs');
  return React__default.createElement("nav", getContainerProps({
    ...props,
    ref,
    role: null,
    'aria-label': ariaLabel
  }), React__default.createElement(StyledBreadcrumb, null, mappedChildren));
});
Breadcrumb.displayName = 'Breadcrumb';

export { Breadcrumb };
